/** @jsxImportSource @emotion/react */
import type { Interpolation, SerializedStyles } from "@emotion/react";
import type { ReactNode } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { radius } from "../../../../../global/emotion";

const pullDownBarStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.onOverlayMedLow,
  borderRadius: radius.xxs,
  cursor: "grab",
  height: "4px",
  width: "30px",
});

export interface PullDownBarProps {
  onPullDown: () => void;
  css?: SerializedStyles;
  threshold?: number;
  children?: ReactNode;
}

export const PullDownBar = ({
  onPullDown,
  css: cssProp,
  threshold = 100,
  children,
}: PullDownBarProps) => {
  const handleMouseDown = (e: React.MouseEvent) => {
    const startY = e.clientY;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startY;

      if (deltaY > threshold) {
        onPullDown();
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        return;
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const startY = e.touches[0].clientY;

    const handleTouchMove = (moveEvent: TouchEvent) => {
      const deltaY = moveEvent.touches[0].clientY - startY;

      if (deltaY > threshold) {
        onPullDown();
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
        return;
      }
    };

    const handleTouchEnd = () => {
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };

    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleTouchEnd);
  };

  return (
    <div
      css={[pullDownBarStyle, cssProp]}
      onClick={() => onPullDown()}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {children}
    </div>
  );
};
